import React, { useCallback, useEffect, useState } from 'react'
import UserLayout from '../../layouts/UserLayout'
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { getFromStorage } from '../../utils/storage'
import { DOMAIN } from '../../utils/api'
import { handleError } from '../../utils/error'
import axios from 'axios'
import PickDate from '../../components/PickDate'
import OrderStatisticsTable from '../../components/OrderStatisticsTable'
import { Bar } from 'react-chartjs-2'
import PickDateWithoutShift from '../../components/PickDateWithoutShift'
import PickDateWithoutShiftGrow from '../../components/PickDateWithoutShiftGrow'
import { currentUserActions } from '../../redux/store'
import { useDispatch } from 'react-redux'
import { mainGreen, mainRed } from '../../utils/color'

export default function StatisticsByStore() {
	const dispatch = useDispatch()
	const min345 = useMediaQuery('(min-width:345px)')
	const navigate = useNavigate()
	const { storeId } = useParams()
	const token = getFromStorage('current-user')?.accessToken
	const [loadingStatistics, setLoadingStatistics] = useState(true)
	const [statistics, setStatistics] = useState([])
	const [timePick, setTimePick] = useState({
		shift: '',
		date: new Date().getDate()?.toString(),
		month: (new Date().getMonth() + 1)?.toString(),
		year: new Date().getFullYear()?.toString(),
	})

	const setTime = (time) => {
		setTimePick(time)
	}

	const [statisticsGrow, setStatisticsGrow] = useState([])
	const currentGrowDate = new Date()
	const weekBeforeCurrentGrowDate = new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7)

	const [firstTimePickGrow, setFirstTimePickGrow] = useState({
		date: weekBeforeCurrentGrowDate.getDate()?.toString(),
		month: (weekBeforeCurrentGrowDate.getMonth() + 1)?.toString(),
		year: weekBeforeCurrentGrowDate.getFullYear()?.toString(),
	})

	const [secondTimePickGrow, setSecondTimePickGrow] = useState({
		date: currentGrowDate.getDate()?.toString(),
		month: (currentGrowDate.getMonth() + 1)?.toString(),
		year: currentGrowDate.getFullYear()?.toString(),
	})

	const setFirstTimeGrow = (time) => {
		setFirstTimePickGrow(time)
	}

	const setSecondTimeGrow = (time) => {
		setSecondTimePickGrow(time)
	}

	const [firstTimePick, setFirstTimePick] = useState({
		date: '',
		month: (new Date().getMonth() + 1)?.toString(),
		year: new Date().getFullYear()?.toString(),
	})

	const [secondTimePick, setSecondTimePick] = useState({
		date: '',
		month: (new Date().getMonth() + 1)?.toString(),
		year: new Date().getFullYear()?.toString(),
	})

	const [statisticsCompare, setStatisticsCompare] = useState([])

	const setFirstTime = (time) => {
		setFirstTimePick(time)
	}

	const setSecondTime = (time) => {
		setSecondTimePick(time)
	}

	useEffect(() => {
		if (firstTimePick.month !== '' && secondTimePick.month === '') {
			setSecondTimePick({
				...secondTimePick,
				month: firstTimePick.month,
			})
		}

		if (firstTimePick.month === '' && secondTimePick.month !== '') {
			setSecondTimePick({
				...secondTimePick,
				month: firstTimePick.month,
			})
		}

		if (firstTimePick.date !== '' && secondTimePick.date === '') {
			setSecondTimePick({
				date: firstTimePick.date,
				month: secondTimePick.month,
				year: secondTimePick.year,
			})
		}

		if (firstTimePick.date === '' && secondTimePick.date !== '') {
			setSecondTimePick({
				date: firstTimePick.date,
				month: secondTimePick.month,
				year: secondTimePick.year,
			})
		}
	}, [firstTimePick, secondTimePick])

	const getStatistics = useCallback(
		async function () {
			const { shift, date, month, year } = timePick
			try {
				const response = await axios({
					url: `${DOMAIN}/admin/statistics/${storeId}?shift=${shift}&date=${date}&month=${month}&year=${year}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
					withCredentials: true,
				})
				setStatistics(response.data)
				setLoadingStatistics(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, storeId, timePick, navigate, dispatch],
	)

	useEffect(() => {
		getStatistics()
	}, [getStatistics, timePick])

	const postCompareStatistics = useCallback(
		async function () {
			try {
				const response = await axios({
					url: `${DOMAIN}/admin/statistics/compare/${storeId}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'POST',
					data: { firstTimePick, secondTimePick },
					withCredentials: true,
				})
				setStatisticsCompare(response.data)
				setLoadingStatistics(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, firstTimePick, secondTimePick, navigate, storeId, dispatch],
	)

	useEffect(() => {
		postCompareStatistics()
	}, [postCompareStatistics])

	const postGrowStatistics = useCallback(
		async function () {
			try {
				const response = await axios({
					url: `${DOMAIN}/admin/statistics/grow/${storeId}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'POST',
					data: { firstTimePickGrow, secondTimePickGrow },
					withCredentials: true,
				})
				setStatisticsGrow(response.data.ordersByStoreGrow)
				if (response.data.message) {
					alert(response.data.message)
				}
				setLoadingStatistics(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, firstTimePickGrow, secondTimePickGrow, navigate, storeId, dispatch],
	)

	useEffect(() => {
		postGrowStatistics()
	}, [postGrowStatistics])

	return (
		<UserLayout>
			<Box sx={{ maxWidth: 992, margin: '0 auto' }}>
				<Box
					sx={{
						padding: '10px',
						background: '#fff',
					}}>
					<Box sx={{ textAlign: 'center' }}>
						<Typography sx={{ fontWeight: 'bold' }}>THỐNG KÊ {statistics?.storeName?.toUpperCase()}</Typography>
					</Box>
					<PickDate setTime={setTime} time={timePick} />
				</Box>
				<Box
					sx={{
						padding: min345 ? '0 10px 10px' : '0 8px 10px',
						background: '#fff',
					}}>
					{loadingStatistics ? (
						<Box display='flex' justifyContent='center' sx={{ mb: 3 }}>
							<CircularProgress sx={{ color: mainGreen }} />
						</Box>
					) : (
						<>
								<OrderStatisticsTable statistics={statistics} />
							<Divider />
							<Box display='flex' justifyContent='center' sx={{ mb: 1, mt: 2 }}>
								<Chip
									sx={{
										color: '#fff',
										background: mainGreen,
									}}
									label='I. TĂNG TRƯỞNG THEO THỜI GIAN'
								/>
							</Box>
							<Box display='flex' justifyContent='center' alignItems='flex-end'>
								<Typography sx={{ mr: 2, pb: 1 }}>Từ</Typography>
								<PickDateWithoutShiftGrow setTime={setFirstTimeGrow} time={firstTimePickGrow} />
							</Box>
							<Box display='flex' justifyContent='center' alignItems='flex-end'>
								<Typography sx={{ mr: 2, pb: 1 }}>đến</Typography>
								<PickDateWithoutShiftGrow setTime={setSecondTimeGrow} time={secondTimePickGrow} />
							</Box>
							<Box display='flex' justifyContent='center'>
								<Button
									sx={{
										textTransform: 'none',
										padding: '0 10px',
										mt: 1,
										background: mainGreen,
										'&:hover': { background: mainGreen },
									}}
									variant='contained'
									size='small'
									onClick={() => {
										postGrowStatistics()
									}}>
									Xác nhận
								</Button>
							</Box>
							<Table sx={{ mb: 3, mt: 2, background: '#fff' }}>
								<TableHead sx={{ background: mainGreen, border: 'none' }}>
									<TableRow>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
											}}>
											Thanh toán
										</TableCell>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
											}}>
											Số đơn
										</TableCell>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
											}}>
											Doanh thu (đ)
										</TableCell>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
											}}>
											Sản lượng (kg)
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<TableRow>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											Tiền mặt
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											{statisticsGrow?.reduce((total, order) => (total += order.cashOrders.order), 0).toLocaleString()}
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											{statisticsGrow
												?.reduce((total, order) => (total += order.cashOrders.revenue), 0)
												.toLocaleString()}
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											{statisticsGrow?.reduce((total, order) => (total += +order.cashOrders.quantity), 0).toFixed(1)}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											Chuyển khoản
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											{statisticsGrow
												?.reduce((total, order) => (total += order.transferOrders.order), 0)
												.toLocaleString()}
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											{statisticsGrow
												?.reduce((total, order) => (total += order.transferOrders.revenue), 0)
												.toLocaleString()}
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											{statisticsGrow
												?.reduce((total, order) => (total += +order.transferOrders.quantity), 0)
												.toFixed(1)}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											App
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											{statisticsGrow?.reduce((total, order) => (total += order.appOrders.order), 0).toLocaleString()}
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											{statisticsGrow?.reduce((total, order) => (total += order.appOrders.revenue), 0).toLocaleString()}
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
											}}>
											{statisticsGrow?.reduce((total, order) => (total += +order.appOrders.quantity), 0).toFixed(1)}
										</TableCell>
									</TableRow>
									<TableRow sx={{ background: mainRed }}>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											TỔNG
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											{statisticsGrow?.reduce((total, order) => (total += order.statistics.order), 0).toLocaleString()}
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											{statisticsGrow
												?.reduce((total, order) => (total += order.statistics.revenue), 0)
												.toLocaleString()}
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											{statisticsGrow?.reduce((total, order) => (total += +order.statistics.quantity), 0).toFixed(1)}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
							<Bar
								style={{ marginBottom: '10px', marginTop: '10px' }}
								data={{
									labels: statisticsGrow?.map((date) => date.date),
									datasets: [
										{
											label: 'Sản lượng (kg)',
											data: statisticsGrow?.map((date) => date.statistics.quantity),
											backgroundColor: [mainRed],
											borderWidth: 1,
											borderColor: '#000',
										},
									],
								}}
								options={{
									animation: { duration: 500 },
								}}
							/>
							<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
								<Typography
									sx={{
										fontSize: '14px',
										background: mainGreen,
										color: '#fff',
										padding: '0 10px',
										borderRadius: '5px',
									}}>
									Tăng trưởng sản lượng
								</Typography>
							</Box>
							<Divider />
							<Bar
								style={{ marginBottom: '10px', marginTop: '10px' }}
								data={{
									labels: statisticsGrow?.map((date) => date.date),
									datasets: [
										{
											label: 'Doanh thu (ngàn đồng)',
											data: statisticsGrow?.map((date) => date.statistics.revenue / 1000),
											backgroundColor: [mainGreen],
											borderWidth: 1,
											borderColor: '#000',
										},
									],
								}}
								options={{
									animation: { duration: 500 },
								}}
							/>
							<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
								<Typography
									sx={{
										fontSize: '14px',
										background: mainGreen,
										color: '#fff',
										padding: '0 10px',
										borderRadius: '5px',
									}}>
									Tăng trưởng doanh thu
								</Typography>
							</Box>
							<Divider />
							<Box display='flex' justifyContent='center' sx={{ mb: 1, mt: 2 }}>
								<Chip
									sx={{
										color: '#fff',
										background: mainGreen,
									}}
									label='II. SO SÁNH GIỮA 2 THỜI ĐIỂM'
								/>
							</Box>
							<PickDateWithoutShift
								timeLabel={1}
								labelBackground={mainGreen}
								labelColor={'#fff'}
								setTime={setFirstTime}
								time={firstTimePick}
							/>
							<PickDateWithoutShift
								timeLabel={2}
								labelBackground={'#FFAB00'}
								labelColor={'#000'}
								setTime={setSecondTime}
								time={secondTimePick}
							/>
							<Box display='flex' justifyContent='center'>
								<Button
									sx={{
										textTransform: 'none',
										padding: '0 10px',
										mt: 1,
										background: mainGreen,
										'&:hover': { background: mainGreen },
									}}
									variant='contained'
									size='small'
									onClick={() => {
										postCompareStatistics()
									}}>
									Xác nhận
								</Button>
							</Box>
							<Bar
								style={{ marginBottom: '10px', marginTop: '10px' }}
								data={{
									labels: statisticsCompare?.map((store) => store.storeName),
									datasets: [
										{
											label: `${firstTimePick.date}${firstTimePick.month}${firstTimePick.year}`,
											data: statisticsCompare?.map((store) => store.firstStatistics.firstTotalQuantity),
											backgroundColor: [mainGreen],
											borderWidth: 1,
											borderColor: '#000',
										},
										{
											label: `${secondTimePick.date}${secondTimePick.month}${secondTimePick.year}`,
											data: statisticsCompare?.map((store) => store.secondStatistics.secondTotalQuantity),
											backgroundColor: ['#FFAB00'],
											borderWidth: 1,
											borderColor: '#000',
										},
									],
								}}
								options={{
									animation: { duration: 500 },
								}}
							/>
							<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
								<Typography
									sx={{
										fontSize: '14px',
										background: mainGreen,
										color: '#fff',
										padding: '0 10px',
										borderRadius: '5px',
									}}>
									Sản lượng (kg)
								</Typography>
							</Box>
							<Divider />
							<Bar
								style={{ marginBottom: '10px', marginTop: '10px' }}
								data={{
									labels: statisticsCompare?.map((store) => store.storeName),
									datasets: [
										{
											label: `${firstTimePick.date}${firstTimePick.month}${firstTimePick.year}`,
											data: statisticsCompare?.map((store) => store.firstStatistics.firstTotalRevenue / 1000),
											backgroundColor: [mainGreen],
											borderWidth: 1,
											borderColor: '#000',
										},
										{
											label: `${secondTimePick.date}${secondTimePick.month}${secondTimePick.year}`,
											data: statisticsCompare?.map((store) => store.secondStatistics.secondTotalRevenue / 1000),
											backgroundColor: ['#FFAB00'],
											borderWidth: 1,
											borderColor: '#000',
										},
									],
								}}
								options={{
									animation: { duration: 500 },
								}}
							/>
							<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
								<Typography
									sx={{
										fontSize: '14px',
										background: mainGreen,
										color: '#fff',
										padding: '0 10px',
										borderRadius: '5px',
									}}>
									Doanh thu (ngàn đồng)
								</Typography>
							</Box>
							<Divider />
							<Bar
								style={{ marginBottom: '10px', marginTop: '10px' }}
								data={{
									labels: statisticsCompare?.map((store) => store.storeName),
									datasets: [
										{
											label: `${firstTimePick.date}${firstTimePick.month}${firstTimePick.year}`,
											data: statisticsCompare?.map((store) => store.firstStatistics.firstTotalOrdered),
											backgroundColor: [mainGreen],
											borderWidth: 1,
											borderColor: '#000',
										},
										{
											label: `${secondTimePick.date}${secondTimePick.month}${secondTimePick.year}`,
											data: statisticsCompare?.map((store) => store.secondStatistics.secondTotalOrdered),
											backgroundColor: ['#FFAB00'],
											borderWidth: 1,
											borderColor: '#000',
										},
									],
								}}
								options={{
									animation: { duration: 500 },
								}}
							/>
							<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
								<Typography
									sx={{
										fontSize: '14px',
										background: mainGreen,
										color: '#fff',
										padding: '0 10px',
										borderRadius: '5px',
									}}>
									Số đơn
								</Typography>
							</Box>
							<Divider />
						</>
					)}
				</Box>
			</Box>
		</UserLayout>
	)
}
